/**
 * CallRecordingPlayer — compact inline audio player for call recordings
 *
 * Loads audio from /api/telephony/recording?callId={id} via proxy with an
 * Authorization-header + blob-URL (zelfde patroon als VoicemailList): een
 * ?token= in de audio-src lekt de JWT via logs/history, dus dat pad is weg.
 * Shows play/pause, progress bar, and duration.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

interface CallRecordingPlayerProps {
  callId: string;
  durationSeconds?: number;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function CallRecordingPlayer({ callId, durationSeconds }: CallRecordingPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds || 0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Fetch met Authorization-header → blob-URL (het <audio>-element kan zelf
  // geen headers zetten). Lazy: pas bij de eerste play-klik, niet on-mount.
  const loadAudio = useCallback(async (): Promise<string | null> => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return null;
      const r = await fetch(`/api/telephony/recording?callId=${encodeURIComponent(callId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return null;
      const blob = await r.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, [callId]);

  // Blob-URL vrijgeven bij unmount/vervanging.
  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  const handlePlay = useCallback(() => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    const startPlayback = () => {
      if (!audioRef.current) return;
      setIsLoading(true);
      audioRef.current.play().then(() => {
        setIsLoading(false);
        setIsPlaying(true);
      }).catch(() => {
        setIsLoading(false);
        setError(true);
      });
    };

    if (audioUrl) {
      startPlayback();
      return;
    }

    setIsLoading(true);
    loadAudio().then((url) => {
      if (!url) {
        setIsLoading(false);
        setError(true);
        return;
      }
      setAudioUrl(url);
      // Wacht één tick zodat het <audio>-element de nieuwe src heeft.
      requestAnimationFrame(startPlayback);
    });
  }, [isPlaying, audioUrl, loadAudio]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current && audioRef.current.duration && isFinite(audioRef.current.duration)) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = fraction * duration;
    setCurrentTime(fraction * duration);
  }, [duration]);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setError(true);
  }, []);

  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Opname niet beschikbaar</span>
      </div>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-2 py-1">
      {/* Hidden audio element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="none"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onError={handleError}
        />
      )}

      {/* Play/Pause button */}
      <button
        onClick={handlePlay}
        disabled={isLoading}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors flex-shrink-0 disabled:opacity-50"
      >
        {isLoading ? (
          <div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
        ) : isPlaying ? (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* Current time */}
      <span className="text-xs text-slate-500 font-mono w-8 text-right flex-shrink-0">
        {isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(0)}
      </span>

      {/* Progress bar */}
      <div
        className="flex-1 h-1.5 bg-slate-200 rounded-full cursor-pointer relative min-w-[60px]"
        onClick={handleSeek}
      >
        <div
          className="h-full bg-indigo-500 rounded-full transition-[width] duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Duration */}
      <span className="text-xs text-slate-400 font-mono w-8 flex-shrink-0">
        {formatTime(duration)}
      </span>
    </div>
  );
}
